import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnInit, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { LoggingService } from '@app/core/services/logging/logging.service';
import { UserService } from '@app/core/services/backend/user.service';
import { SettingsPageService } from '@app/domains/menu/services';
import { InviteUsersModalComponent } from '@app/shared/modals';
import { BookingRepository } from '@app/store';
import {
  IonAvatar,
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonText,
  ModalController,
  ToastController,
} from '@ionic/angular/standalone';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { addIcons } from 'ionicons';
import {
  alertCircle,
  checkmarkCircle,
  hourglass,
  mail,
  trash,
  informationCircle,
} from 'ionicons/icons';
import { Subscription, filter, firstValueFrom, take } from 'rxjs';
import { AccountRequiredComponent } from '../account-required/account-required.component';

@Component({
  selector: 'app-invite-users-to-stay',
  templateUrl: './invite-users-to-stay.component.html',
  styleUrls: ['./invite-users-to-stay.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonButton,
    IonButton,
    IonIcon,
    IonText,
    TranslocoPipe,
    IonItem,
    IonInput,
    IonLabel,
    IonIcon,
    IonText,
    IonList,
    IonAvatar,
    IonBadge,
    IonCard,
    IonCardHeader,
    IonCardTitle,
    IonCardSubtitle,
    IonCardContent,
    IonListHeader,
  ],
})
export class InviteUsersToStayComponent implements OnInit, OnDestroy {
  @Input() size: 'small' | 'normal' = 'normal';
  @Input() presentingElement!: HTMLElement | undefined;

  public currentBooking$ = this._bookingRepo.activeBooking$;
  public readonly currentUser$ = this._settingsPageService.currentUser$;

  public bookingId!: string;
  public form!: FormGroup;
  public isLoading = false;
  public invitationsSent = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private _fb: FormBuilder,
    private _modalCtrl: ModalController,
    private _transloco: TranslocoService,
    private _userService: UserService,
    private _bookingRepo: BookingRepository,
    private _toastCtrl: ToastController,
    private _settingsPageService: SettingsPageService,
    private readonly elementRef: ElementRef,
  ) {
    addIcons({
      informationCircle,
      trash,
      mail,
      checkmarkCircle,
      alertCircle,
      hourglass,
    });
  }

  ngOnInit() {
    this._bookingRepo.activeBooking$
      .pipe(
        filter((booking) => booking !== null),
        take(1),
      )
      .subscribe((booking) => {
        if (booking) {
          this.bookingId = booking._id;
        } else {
          this.bookingId = '';
        }
      });

    this.form = this._fb.group({
      email: ['', [Validators.required, this.strictEmailValidator.bind(this)]],
    });
  }

  /**
   * Custom email validator that enforces the format: something@something.something
   */
  private strictEmailValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(control.value.trim());

    return isValid ? null : { email: { value: control.value } };
  }

  markAsTouched() {
    this.form.get('email')?.markAsTouched();
  }

  async onConfirm() {
    if (this.form.valid) {
      const email = this.form.get('email')?.value?.trim();

      if (!email) {
        return;
      }
      const currentUser = await firstValueFrom(this.currentUser$);

      if (currentUser?.isAnonymous) {
        const modal = await this._modalCtrl.create({
          component: AccountRequiredComponent,
          presentingElement: this.elementRef.nativeElement,
          // breakpoints: [0.9],
          // initialBreakpoint: 0.7,
          // handle: false,
        });

        await modal.present();
        return;
      }

      this.isLoading = true;

      const subscription = this._userService
        .sendBookingInvitations(this.bookingId, [email])
        .subscribe(
          (booking) => {
            this.invitationsSent = true;
            LoggingService.log('Invitation sent', {
              bookingId: this.bookingId,
              email: email,
            });

            this._bookingRepo.upsertBooking(booking);

            this._toastCtrl
              .create({
                message: this._transloco.translate(
                  'domain.property.modal.inviteUsers.success',
                ),
                color: 'dark',
                duration: 3000,
              })
              .then((toast) => toast.present());
            this.form.reset();
            this.isLoading = false;
          },
          (error) => {
            this._toastCtrl
              .create({
                message: this._transloco.translate(
                  'domain.property.modal.inviteUsers.error',
                ),
                duration: 3000,
              })
              .then((toast) => toast.present());
            LoggingService.log('Error sending invitation', error);
            this.isLoading = false;
          },
        );
      this.subscriptions.push(subscription);
    }
  }

  async openInviteModal() {
    const currentUser = await firstValueFrom(this.currentUser$);

    if (currentUser?.isAnonymous) {
      const modal = await this._modalCtrl.create({
        component: AccountRequiredComponent,
        presentingElement: this.presentingElement,
        // breakpoints: [0.9],
        // initialBreakpoint: 0.7,
        // handle: false,
      });

      await modal.present();
      return;
    }
    const modal = await this._modalCtrl.create({
      component: InviteUsersModalComponent,
      componentProps: {
        bookingId: this.bookingId,
        userService: this._userService,
      },
      presentingElement: this.presentingElement,
    });

    modal.present();
  }

  removeInvitation(email: string) {
    this._toastCtrl
      .create({
        message: this._transloco.translate(
          'domain.property.modal.inviteUsers.removed',
        ),
        color: 'dark',
        duration: 3000,
      })
      .then((toast) => toast.present());
  }

  ngOnDestroy() {
    for (const subscription of this.subscriptions) subscription.unsubscribe();
  }
}
